import './index.scss'
import {PageTitle, ResponsivePageTitle} from '../../components/core/title'
import {ImgButton} from '../../components/core/button'
import {
    ActivityTrackerIcon,
    EditIcon,
    HorizontalMoreIcon,
    IntegrationIcon,
    PausedIcon,
    PlayIcon,
    PlusIcon,
    SwitchArrowIcon,
    TrashIcon
} from '../../icons'
import {
    deleteResources,
    getResources,
    toggleResourcesStatus
} from '../../stores/resources'
import {useDispatch, useSelector} from 'react-redux'
import AddResourceModal from '../../components/pages/resource/AddResourceModal'
import Pulse from '../../components/core/pulse'
import DonutChart from '../../components/pages/monitoring/DonutChart'
import Footer from '../../components/layouts/footer'
import LogsTable from '../../components/pages/monitoring/LogsTable'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useEffect, useRef, useState} from 'react'
import {
    getResource,
    getResourceData,
    getResourceRegions,
    getResourceWithChangedState,
    resetQuery,
    setIsMonitoringLoading,
    setQuery,
    setRangeSelected,
    setResource,
    setResourceLogs,
    setResponseLogs,
    setShowSectionLoader
} from '../../stores/monitoring'
import LineChart from '../../components/pages/monitoring/LineChart'
import {addMinutes, differenceInDays, differenceInHours, differenceInMonths, differenceInYears} from 'date-fns'
import Additional from '../../components/pages/monitoring/Additional'
import localStorageManager from '../../utils/localStorageManager'
import localStorageKeys from '../../utils/localStorageKeys'
import {BlurBackground, TutorialActions} from '../../components/core/tutorial'
import {routes} from '../../routes'
import NavigationHelper from './../../utils/navigation'
import useOutsideClick from '../../utils/hooks/useOutsideClick'
import Countdown from 'react-countdown'
import plans from './../../data/plans.json'
import SslAndDomain from '../../components/pages/monitoring/SslAndDomain'
import {timesDataFactory} from '../../components/pages/monitoring/times'
import CustomSelect from '../../components/core/customSelect'
import {locationManager} from '../../utils/locations'
import classNames from 'classnames'
import EditResourceModal from '../../components/pages/resource/EditResourceModal'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import Confirm from '../../components/core/confirm'
import {setConfirmIsActive} from '../../stores/alert'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import Customer from '../../utils/customer'
import {getTeams} from '../../stores/teams'
import MonitorDetails from '../../components/pages/monitoring/MonitorDetails'
import {ResourceUrl} from '../resources'
import {colors} from '../../utils/colors'
import renderSkeletonLoaders from '../../utils/loader'
import SectionLoader from '../../components/core/sectionLoader'
import Spinner from '../../components/core/spinner'
import {isResourceLimitEnabled} from '../../utils/limits'
import DateInterval from '../../components/pages/dateInterval'
import useInterval from 'use-interval'
import ResponseLogsTable from '../../components/pages/monitoring/ResponseLogsTable'
import Modal from '../../components/core/modal'
import {setActivePopup} from '../../stores/popup'
import Badge from '../../components/core/badge'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {getAllLimitUsages} from '../../stores/auth'
import {envConfig} from '../../utils/env'

const UPTIME_MONITORING = 'uptime_monitoring'
const {
    firstDataWaitingTime,
    liveUpdateTime,
    isIntegrationEnabled,
    isReadOnlyModeEnabled
} = envConfig

const Monitoring = () => {
    const {id} = useParams()
    const text = useNamespaceTranslations('monitoring')
    const modalTypes = useNamespaceTranslations('modals')
    const usageLimits = useNamespaceTranslations('usage-limits')
    const baseTimes = useNamespaceTranslations('date-interval').base_times
    const navigate = useNavigate()
    const times = timesDataFactory(UPTIME_MONITORING, baseTimes)
    const isMonitoringLoading = useSelector(state => state.monitoring.isMonitoringLoading)
    const chartData = useSelector(state => state.monitoring.data)
    const resource = useSelector(state => state.monitoring.resource)
    const categories = useSelector(state => state.monitoring.categories)
    const rangeSelected = useSelector(state => state.monitoring.rangeSelected)
    const query = useSelector(state => state.monitoring.query)
    const activeTimeIndex = useSelector(state => state.monitoring.activeTimeIndex)
    const showSectionLoader = useSelector(state => state.monitoring.showSectionLoader)
    const logs = useSelector(state => state.monitoring.resourceLogs)
    const responseLogs = useSelector(state => state.monitoring.responseLogs)
    const resources = useSelector(state => state.resources.resources)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const activePopup = useSelector(state => state.popup.activePopup)
    const currentPlan = localStorage.getItem('plan')
    const [region, setRegion] = useState(null)
    const [combinedState, setCombinedState] = useState({query: query, region: region})
    const [selectRegions, setSelectRegions] = useState([])
    const [isRegionsFetching, setIsRegionsFetching] = useState(true)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [isEdited, setIsEdited] = useState(false)
    const [actionLoaders, setActionLoader] = useState({})
    const [isAllRegionShowed, setIsAllRegionShowed] = useState(false)
    const [lineChartData, setLineChartData] = useState([])
    const [regionsLoader, setRegionsLoader] = useState(false)
    const [isChartLoading, setIsChartLoading] = useState(false)
    const [titleTextValue, setTitleTextValue] = useState('')
    const [isFiltering, setIsFiltering] = useState(false)
    const timingSettings = {
        intervalChangerLimit: 3,
        subtractedMinutes: 1,
        subtractedSeconds: 60,
        subtractedMinutesForDateRange: 1,
        minLimitForDayAndHour: 1,
        addedHoursForDateRange: 24,
        maxLimitForDayInterval: 180,
        maxLimitForMonthInterval: 1095
    }
    const [activeTip, setActiveTip] = useState('chart')
    const [showTips, setShowTips] = useState(true)
    const [isResourceSwitcherActive, setIsResourceSwitcherActive] = useState(false)
    const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(localStorageManager.get(localStorageKeys.IS_AUTOREFRESH_ENABLED))
    const resourceSwitcherRef = useRef()

    useOutsideClick(resourceSwitcherRef, () => setIsResourceSwitcherActive(false))
    const dispatch = useDispatch()

    const openModal = (modalData) => {
        dispatch(setActivePopup(modalData))
    }

    const closeModal = () => dispatch(setActivePopup(null))

    const getData = async(showLoader = true) => {
        await dispatch(getResourceData(id, query, region?.value, navigate, showLoader))
        document.querySelectorAll('.apexcharts-reset-icon').forEach(item => {
            item.click()
        })
    }
    const handleChangeTime = async(data) => {
        const startDate = new Date(data.startDate)
        const endDate = new Date(data.endDate)

        if (data?.limit) {
            setTitleTextValue('')
            await dispatch(setQuery({
                type: 'time',
                item: {
                    date: data.startDate,
                    interval: data.interval,
                    limit: data.limit
                },
                showLoader: data.showLoader
            }))
            return
        }

        const differenceDays = differenceInDays(endDate, startDate)
        const differenceHours = differenceInHours(endDate, startDate)
        setTitleTextValue(text.content_body_range)

        const rangeConfig = {
            year: {
                limit: differenceInYears(endDate, startDate),
                interval: 'year'
            },
            month: {
                limit: differenceInMonths(endDate, startDate),
                interval: 'month'
            },
            day: {
                limit: differenceDays,
                interval: 'day'
            }
        }

        if (differenceDays > timingSettings.intervalChangerLimit) {
            const config =
                differenceDays > timingSettings.maxLimitForMonthInterval
                    ? rangeConfig.year
                    : differenceDays > timingSettings.maxLimitForDayInterval
                        ? rangeConfig.month
                        : rangeConfig.day

            await dispatch(setQuery({
                type: 'date',
                item: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    limit: config.limit
                },
                interval: config.interval,
                showLoader: data.showLoader
            }))
            return
        }

        if (differenceDays === 0 && differenceHours < timingSettings.addedHoursForDateRange / 2) {
            await dispatch(setQuery({
                type: 'date',
                interval: 'minute',
                item: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    limit: Math.max(differenceHours * 60, 60)
                },
                showLoader: data.showLoader
            }))
            return
        }

        await dispatch(setQuery({
            type: 'date',
            interval: 'hour',
            item: {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                limit:
                    differenceDays === timingSettings.intervalChangerLimit ||
                    differenceHours > timingSettings.addedHoursForDateRange
                        ? differenceDays * 24
                        : differenceHours
            },
            showLoader: data.showLoader
        }))
    }

    const getTitleText = () => {
        return titleTextValue || text.content_body_last.replace(':val', text.times_data.find((item, index) => index === activeTimeIndex))
    }
    const getPulseColor = () => {
        return !resource?.state ? 'red' : ''
    }
    const resetDateRange = () => {
        setTitleTextValue('')
        dispatch(resetQuery())
    }

    const getFirstData = () => {
        const date = new Date()
        dispatch(setQuery({
            type: 'date',
            interval: 'minute',
            item: {
                startDate: addMinutes(date, -timingSettings.subtractedSeconds - timingSettings.subtractedMinutes).toISOString(),
                endDate: addMinutes(date, -timingSettings.subtractedMinutes).toISOString(),
                limit: times[activeTimeIndex].limit
            }
        }))
    }

    const getCountDownText = () => {
        const time = firstDataWaitingTime - getTimeDiff()
        return <Countdown onComplete={getFirstData} date={Date.now() + time}
            renderer={({minutes, seconds}) => {
                return <span className={classNames('count-down-text', {
                    dark: isDarkMode
                })}>{text.please_wait.replace(':val', `${minutes} ${text.minutes_text} ${seconds} ${text.seconds_text}`)}</span>
            }
            }/>
    }

    const getCollectDataText = () => {
        let timeDiff = getTimeDiff()
        if (timeDiff > 0) {
            return timeDiff < firstDataWaitingTime ? getCountDownText() : ''
        }
        return ''
    }

    const getTimeDiff = () => {
        return Date.now() - localStorageManager.get(localStorageKeys.CREATED_RESOURCES)?.[id]
    }

    const getResourceStateIcon = () => {
        return resource.enabled ? <Pulse color={getPulseColor()}/> : <PausedIcon stroke={colors.colorOrange}/>
    }

    const generateResourcePopUp = () => {
        return (
            <div className={classNames('popover', {
                dark: isDarkMode
            })}>
                {resources.map((item, index) => {
                    return (
                        <Link to={NavigationHelper.getNavigationUrl(NavigationHelper.getLocalizedPath(routes.monitoring.paths), ':id', item?.resource_id)}
                            onClick={() => setIsResourceSwitcherActive(false)} key={index}
                            className={`popover-item ${id === item?.resource_id ? 'active' : ''}`}>{item.name}</Link>
                    )
                })}
            </div>
        )
    }

    const openSwitcherPopover = () => {
        setIsResourceSwitcherActive(!isResourceSwitcherActive)
    }

    const handleAutorefresh = e => {
        if (e.target.checked) {
            setIsAutoRefreshEnabled(true)
            localStorage.setItem(localStorageKeys.IS_AUTOREFRESH_ENABLED, true)
            return
        }
        setIsAutoRefreshEnabled(false)
        localStorage.setItem(localStorageKeys.IS_AUTOREFRESH_ENABLED, false)
    }

    const handleCloseModal = () => {
        closeModal()
        setIsEdited(false)
    }

    const pauseMonitoring = async() => {
        setActionLoader({
            pause: true
        })
        await dispatch(toggleResourcesStatus([resource?.id], {enabled: false}))
        await dispatch(getResource(id, region))
        setActionLoader({})
    }

    const playMonitoring = async() => {
        setActionLoader({
            monitor: true
        })
        await dispatch(toggleResourcesStatus([resource?.id], {enabled: true}))
        await dispatch(getResource(id, region))
        setActionLoader({})
    }

    const deleteResource = async() => {
        setActionLoader({
            delete: true
        })
        const success = await dispatch(deleteResources([resource?.id]))
        setActionLoader({})
        if (success) {
            dispatch(setConfirmIsActive(false))
            await dispatch(getResources(false))
            navigate(NavigationHelper.getLocalizedPath(routes.resources.paths))
        }
    }

    const getIntegrationButton = () => {
        return  <Dropdown menuButton={<ImgButton className="table-action-btn"> <IntegrationIcon className="card-icon"/></ImgButton>}>
            <MenuItem onClick={() => navigate(NavigationHelper.getLocalizedPath(routes.team_channels.paths))}>
                <span>{text.team_channels}</span>
            </MenuItem>
            <MenuItem onClick={() => navigate(`${NavigationHelper.getLocalizedPath(routes.notifications.paths)}`)}>
                <span>{text.personal_channels}</span>
            </MenuItem>
            {isIntegrationEnabled && <MenuItem onClick={() => navigate(NavigationHelper.getNavigationUrl(
                NavigationHelper.getLocalizedPath(routes.resource_integrations.paths), ':id', id))}>
                <span>{text.integrations}</span>
            </MenuItem>}
        </Dropdown>
    }

    const handleShowAllRegions = () => {
        setIsAllRegionShowed(!isAllRegionShowed)
    }

    const getActionButtons = () => {
        return resource && (
            <Dropdown menuButton={<ImgButton className="table-action-btn"><HorizontalMoreIcon/></ImgButton>}>
                <MenuItem onClick={() => openModal(modalTypes.uptime_monitoring.editResource)} disabled={resource?.is_invalid || !isResourceLimitEnabled(resource, usageLimits) || isReadOnlyModeEnabled}>
                    <span className="d-flex"><EditIcon/></span>
                    <span>{text.edit}</span>
                </MenuItem>
                <MenuItem disabled={resource?.is_invalid || actionLoaders?.pause || !resource?.enabled || isReadOnlyModeEnabled} onClick={pauseMonitoring}>
                    <span className="d-flex">
                        {actionLoaders?.pause ? <ButtonLoader/> : <PausedIcon/>}
                    </span>
                    <span>{text.pause}</span>
                </MenuItem>
                <MenuItem disabled={resource?.is_invalid || actionLoaders?.monitor || resource?.enabled || !isResourceLimitEnabled(resource, usageLimits) || isReadOnlyModeEnabled} onClick={playMonitoring}>
                    <span className="d-flex">
                        {actionLoaders?.monitor ? <ButtonLoader/> : <PlayIcon/>}
                    </span>
                    <span>{text.monitor}</span>
                </MenuItem>
                <MenuDivider/>
                <MenuItem className="danger" onClick={() => {
                    dispatch(setConfirmIsActive(true))
                }
                } disabled={isReadOnlyModeEnabled}>
                    <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                    <span>{text.delete}</span>
                </MenuItem>
            </Dropdown>
        )
    }

    const getBody = () => {
        return (
            <>
                {getCollectDataText()}
                <div>
                    <div className="monitoring-sub-title">
                        {isFirstLoad ? renderSkeletonLoaders(1) : getRegionSelect()}
                        <div className="box-header">
                            {isFirstLoad ? renderSkeletonLoaders(1, 'switch-skeleton') : <><span className={classNames('left-switch', {
                                dark: isDarkMode
                            })} id="monthly-billing">{text.auto_refresh}</span>
                            <span>
                                <label className={classNames('switch', {
                                    dark: isDarkMode
                                })} id="pricing-switcher">
                                    <input type="checkbox" onChange={e => handleAutorefresh(e)}
                                        checked={isAutoRefreshEnabled}/>
                                    <span className={`slider round ${!isAutoRefreshEnabled ? 'off' : ''}`}/>
                                </label>
                            </span></>}
                        </div>
                    </div>
                </div>
                <div className={classNames('monitoring-search', {
                    dark: isDarkMode
                })}>
                    {!resource ? renderSkeletonLoaders(1) :
                        <div className="position-relative">
                            <div className="d-flex gap-1">
                                <div className="resource-name">
                                    {getResourceStateIcon()}
                                    <h2>{resource.name}</h2>
                                    <div className="resource-switcher" ref={resourceSwitcherRef}>
                                        <span onClick={openSwitcherPopover} className="switch-icon">
                                            <SwitchArrowIcon/>
                                        </span>
                                        {isResourceSwitcherActive && generateResourcePopUp()}
                                    </div>
                                </div>
                                {Customer.isOwnTeam() && (
                                    <div className="d-flex gap-half">
                                        {getActionButtons()}
                                        {getIntegrationButton()}
                                    </div>
                                )}
                            </div>
                            <span className={classNames('resource-url', {
                                dark: isDarkMode
                            })}>
                                <ResourceUrl resource={resource}/>
                            </span>
                        </div>}
                    {isFirstLoad ? renderSkeletonLoaders(1) :
                        <div className="monitoring-search-right">
                            <DateInterval
                                callbackFn={(data) => handleChangeTime(data)}
                                resetFn={resetDateRange}
                                isAutoRefreshEnabled={isAutoRefreshEnabled}
                                type={UPTIME_MONITORING}
                                setIsFiltering={setIsFiltering}/>
                        </div>}
                </div>
                <>
                    <div className={classNames('top-section', {
                        dark: isDarkMode
                    })}>
                        <div className="details-container">

                            <div
                                className={classNames({
                                    'content-body loader-container': true,
                                    'has-custom-shadow': showTips && localStorageManager.get(localStorageKeys.SHOW_STATISTICS_TIPS) && activeTip === 'chart',
                                    'dark': isDarkMode
                                })}
                                id="chart">
                                <>
                                    <div className='chart-header'>
                                        <p className={classNames('content-body-text', {
                                            'dark': isDarkMode
                                        })}>{getTitleText()}</p>
                                        <span className="switcher regions-switcher">
                                            {regionsLoader && <Spinner/>}
                                            <span className="left-switch" >{text.show_all_regions}</span>
                                            <span>
                                                <label className="switch" id="pricing-switcher">
                                                    <input type="checkbox" onChange={handleShowAllRegions}
                                                        checked={isAllRegionShowed} disabled={regionsLoader}/>
                                                    <span className={`slider round ${!isAllRegionShowed ? 'off' : ''}`}/>
                                                </label>
                                            </span>
                                        </span>
                                    </div>
                                    {isMonitoringLoading || isChartLoading ? <SectionLoader loader={true}/> : <LineChart
                                        lineChartData={lineChartData}
                                        isDarkMode={isDarkMode}
                                        isAllRegionShowed={isAllRegionShowed}
                                        showRegionsLoader={regionsLoader}
                                        selectRegions={selectRegions}
                                    />}
                                </>
                            </div>
                            <div className="charts-body">
                                <div className={classNames('content-body details-xl', {
                                    dark: isDarkMode
                                })}>
                                    {!resource ? <span className='content-body-loading'>{renderSkeletonLoaders(6)}</span> :
                                        <>
                                            <p className={classNames('content-body-text', {
                                                'dark': isDarkMode
                                            })}>{text.monitor_details}</p>
                                            <MonitorDetails isDarkMode={isDarkMode}/>
                                        </>}
                                </div>
                                <div
                                    className={classNames({
                                        'chart chart-pie loader-container': true,
                                        'has-custom-shadow': showTips && localStorageManager.get(localStorageKeys.SHOW_STATISTICS_TIPS) && activeTip === 'average',
                                        dark: isDarkMode
                                    })}
                                    id="average">
                                    {isMonitoringLoading ? <span className='content-body-loading'>{renderSkeletonLoaders(6)}</span> : <>
                                        <p
                                            className={classNames('content-body-text', {
                                                'dark': isDarkMode
                                            })}>{text.average_statistics}</p>
                                        <Additional
                                            showInfoBox={currentPlan === plans.free_v1.key} isDarkMode={isDarkMode}/>
                                    </>}
                                </div>
                                <div
                                    className={classNames({
                                        'chart chart-pie loader-container': true,
                                        'has-custom-shadow': showTips && localStorageManager.get(localStorageKeys.SHOW_STATISTICS_TIPS) && activeTip === 'percentage',
                                        dark: isDarkMode
                                    })}
                                    id="percentage">
                                    {isMonitoringLoading ? <SectionLoader loader = {true}/> : <>
                                        <p className={classNames('content-body-text', {
                                            'dark': isDarkMode
                                        })}>{text.times}</p>
                                        <DonutChart id={id} isDarkMode={isDarkMode}/>
                                    </>}
                                </div>
                            </div>
                        </div>

                        <div className={classNames('content-body info-body', {
                            dark: isDarkMode
                        })}>
                            {!resource ? <span className='content-body-loading'>{renderSkeletonLoaders(12)}</span> :
                                <>
                                    <p className={classNames('content-body-text', {
                                        'dark': isDarkMode
                                    })}>{text.monitor_details}</p>
                                    <MonitorDetails isDarkMode={isDarkMode}/>
                                </>}
                        </div>
                    </div>
                    <div className={classNames('charts-body', {
                        'display-none': !responseLogs
                    })}>
                        <div
                            className={classNames('response-details logs', {
                                'chart': true,
                                'loader-container': isMonitoringLoading,
                                'dark': isDarkMode
                            })}>
                            {isMonitoringLoading ?
                                <span className='content-body-loading'>{renderSkeletonLoaders(2)}</span> :
                                <>
                                    <div
                                        className={`chart-title ${!responseLogs || responseLogs?.length === 0  ? 'logs-not-found' : ''}`}>
                                        {responseLogs && responseLogs?.length > 0 ?
                                            <>
                                                <span className='response-logs-title'>
                                                    <Badge color='success'>{text.new}</Badge>
                                                    {text.response_logs_title}
                                                </span>
                                                <ResponseLogsTable data={responseLogs}/>
                                            </> : (
                                                <>
                                                    <ActivityTrackerIcon/>
                                                    <span>{text.response_logs_not_found}</span>
                                                </>
                                            )}</div>

                                </>
                            }
                        </div>
                    </div>
                    <div className="charts-body">
                        <div
                            className={classNames('logs', {
                                'chart loader-container': true,
                                'has-custom-shadow': showTips && localStorageManager.get(localStorageKeys.SHOW_STATISTICS_TIPS) && activeTip === 'logs',
                                'dark': isDarkMode
                            })}>
                            {isMonitoringLoading ? <span className='content-body-loading'>{renderSkeletonLoaders(2)}</span> :
                                <>
                                    <div
                                        className={`chart-title ${!logs ? 'logs-not-found' : ''}`}>{logs ?
                                            <>
                                                { text.logs_title }
                                                <LogsTable/>
                                            </> : (
                                                <>
                                                    <ActivityTrackerIcon/>
                                                    <span>{text.logs_not_found}</span>
                                                </>
                                            )}</div>

                                </>}
                        </div>
                    </div>
                    <SslAndDomain id={id} isDarkMode={isDarkMode}/>
                </>
            </>
        )
    }

    const tutorialLastCallback = () => {
        setShowTips(false)
        localStorage.removeItem(localStorageKeys.SHOW_STATISTICS_TIPS)
        localStorage.removeItem(localStorageKeys.NO_RESOURCE_CREATED_KEY)
    }

    const tutorialCalback = element => {
        document.querySelector(`#${element}`).scrollIntoView({
            behavior: 'smooth'
        })
        setActiveTip(element)
    }

    const skipCallback = () => {
        setShowTips(false)
        localStorage.removeItem(localStorageKeys.SHOW_STATISTICS_TIPS)
        localStorage.removeItem(localStorageKeys.NO_RESOURCE_CREATED_KEY)
    }

    const tutorialActions = [
        {
            text: text.tutorial_response_time_desc,
            callBack: () => tutorialCalback('percentage')
        },
        {
            text: text.tutorial_up_down_percent_desc,
            callBack: () => tutorialCalback('average')
        },
        {
            text: text.tutorial_general_average_desc,
            callBack: () => tutorialCalback('logs')
        },
        {
            text: text.tutorial_state_changes_desc,
            callBack: () => tutorialLastCallback()
        }
    ]

    const getRegionSelect = () => {
        return (
            <div className="select-region">
                <CustomSelect
                    className="regions"
                    placeholder={text.select_region}
                    options={locationManager.getMainLocations()}
                    value={region}
                    isOptionDisabled={option => !selectRegions?.map(item => item?.value).includes(option?.value)}
                    name="region"
                    infoText={text.select_region_info}
                    onChange={(name, value) => {
                        setRegion(value)
                        window.history.pushState(null, '', `${window.location.pathname}?region=${value?.value}`)
                        dispatch(setShowSectionLoader(true))
                    }}/>
            </div>
        )
    }

    const fetchData = async() => {
        combinedState.query && combinedState.region && await getData(isEdited ? false : showSectionLoader)
        await dispatch(getTeams())
        setIsFirstLoad(false)
        setIsEdited(false)
    }

    useEffect(() => {
        setIsChartLoading(false)
    }, [isAutoRefreshEnabled])

    useEffect(() => {
        if (selectRegions?.length === lineChartData.length) {
            setRegionsLoader(false)
        }
    }, [lineChartData])

    const fetchDataforAllRegions = async() => {
        setRegionsLoader(true)
        const chartData = []
        let mergedLogs = []
        let mergedResponseLogs = []
        let hasAnyLogs = false
        let hasAnyResponseLogs = false

        for (const selectRegion of selectRegions) {
            const isSelectedRegion = selectRegion?.value === region?.value
            const result = await dispatch(getResourceData(id, query, selectRegion?.value, navigate, isChartLoading, isSelectedRegion, true))

            chartData.push({
                ...result,
                region: selectRegions?.find(r => r?.value === result?.region)?.label
            })

            setIsChartLoading(false)

            if (result?.logs?.length) {
                hasAnyLogs = true
                mergedLogs = [...mergedLogs, ...result.logs]
            }

            if (result?.responseLogs?.length) {
                hasAnyResponseLogs = true
                mergedResponseLogs = [...mergedResponseLogs, ...result.responseLogs]
            }
        }

        dispatch(setResourceLogs({
            logs: hasAnyLogs ? mergedLogs : null,
            state_changed_count: null
        }))

        dispatch(setResponseLogs(hasAnyResponseLogs ? mergedResponseLogs : null))

        setLineChartData(chartData)
    }

    useEffect(() => {
        setLineChartData([{
            region: region?.label,
            data: chartData,
            categories
        }])
    }, [chartData])

    useEffect(() => {
        setCombinedState({query, region})
    }, [query, region])

    useEffect(() => {
        setIsChartLoading(isAllRegionShowed)
    }, [rangeSelected, isAllRegionShowed, isFiltering, region])

    useEffect(() => {
        if (!isRegionsFetching) {
            isAllRegionShowed ? fetchDataforAllRegions() : fetchData()
        }
    }, [combinedState, combinedState, isAllRegionShowed, isRegionsFetching, selectRegions])

    useEffect(() => {
        const updateData = async() => {
            const queryParams = new URLSearchParams(location.search)
            const regionParamValue = queryParams.get('region')
            const selectedRegion = regionParamValue && selectRegions.some(region => region.value === regionParamValue)
                ? locationManager.getMainLocations().find(item => item.value === regionParamValue)
                : selectRegions[0]

            if (!!selectRegions?.length && !selectRegions.some(region => region.value === regionParamValue)) {
                queryParams.delete('region')
                window.history.pushState(null, '', `${window.location.pathname}`)
            }

            setRegion(selectedRegion)
        }

        updateData()
    }, [selectRegions, location.search])

    const fetchSelectRegions = async() => {
        setSelectRegions(await dispatch(getResourceRegions(id, navigate)))
        setIsRegionsFetching(false)
    }

    useEffect(() => {
        dispatch(setRangeSelected(false))
        dispatch(getAllLimitUsages())
        setIsRegionsFetching(true)
        setLineChartData([])
        setRegion(null)
        dispatch(setResource(null))
        fetchSelectRegions()
        dispatch(setIsMonitoringLoading(true))
    }, [id])

    useEffect(() => {
        if (isFirstLoad) dispatch(getAllLimitUsages())
    }, [isFirstLoad])

    useEffect(() => {
        localStorage.getItem('serviceId') && dispatch(getResources(true))
    }, [localStorage.getItem('serviceId')])

    useInterval(() => {
        if (rangeSelected || isAutoRefreshEnabled) {
            return
        }
        dispatch(getResourceWithChangedState(id, region))
    }, liveUpdateTime)

    const tutorialIsActive = showTips && localStorageManager.get(localStorageKeys.SHOW_STATISTICS_TIPS) && chartData.length > 1

    return (
        <>
            {activePopup?.type === modalTypes.uptime_monitoring.seeMoreResponseLogs.type && <Modal
                isOpen={!!activePopup?.type}
                closeModal={closeModal}
                id="large-modal"
                contentClassName="overflow-auto">

            </Modal>}
            {tutorialIsActive &&
                <>
                    <BlurBackground/>
                    <TutorialActions actions={tutorialActions}
                        skipCallback={skipCallback}/>
                </>
            }
            <main className={classNames({
                'main monitoring': true,
                'overflow-hidden': tutorialIsActive,
                dark: isDarkMode
            })}>
                <div>
                    <ResponsivePageTitle/>
                    <div className="content-title">
                        {activePopup?.component === modalTypes.uptime_monitoring.editResource.component &&
                            <EditResourceModal resource={{
                                ...resource,
                                resource_id: resource?.id
                            }}
                            setIsEdited={setIsEdited}
                            setSelectRegions={setSelectRegions}
                            contentClassName="overflow-auto" title={activePopup.title}
                            isOpen={!!activePopup?.type}
                            closeModal={handleCloseModal}
                            />
                        }
                        <Confirm
                            body={text.are_you_sure}
                            callback={deleteResource} showLoader={actionLoaders?.delete}/>
                        <div className="left-side">
                            <div>
                                <span className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</span>
                                <PageTitle>
                                    <h1  className={classNames('page-title', {
                                        dark: isDarkMode
                                    })}>{text.title}</h1>
                                </PageTitle>
                            </div>
                        </div>
                        {Customer.isOwnTeam() && (
                            isFirstLoad ? renderSkeletonLoaders(1, 'btn-skeleton min-width add-btn-loader') :
                                <ImgButton onClick={() => openModal(modalTypes.uptime_monitoring.addResource)} disabled={isReadOnlyModeEnabled}>
                                    {text.add_resource}
                                    <span><PlusIcon/></span>
                                </ImgButton>
                        )}
                    </div>
                    <div className="monitoring-search">
                        <div className="box-header-mobile">
                            {isFirstLoad ? renderSkeletonLoaders(1, 'switch-skeleton') : <>
                                <span className={classNames('left-switch', {
                                    dark: isDarkMode
                                })} id="monthly-billing">{text.auto_refresh}</span>
                                <span>
                                    <label className="switch" id="pricing-switcher">
                                        <input type="checkbox" onChange={e => handleAutorefresh(e)}
                                            checked={isAutoRefreshEnabled}/>
                                        <span className={`slider round ${!isAutoRefreshEnabled ? 'off' : ''}`}/>
                                    </label>
                                </span>
                            </>}
                        </div>
                        {Customer.isOwnTeam() && (
                            <div className="search-box">
                                {isFirstLoad ? renderSkeletonLoaders(1, 'btn-skeleton responsive-add-button') :
                                    <ImgButton onClick={() => openModal(modalTypes.uptime_monitoring.addResource)} className="responsive-add-button">
                                        <span><PlusIcon/></span>
                                    </ImgButton>}
                            </div>
                        )}
                    </div>
                    {getBody()}
                </div>
                <Footer/>
            </main>
            {activePopup?.component === modalTypes.uptime_monitoring.addResource.component &&
                <AddResourceModal
                    contentClassName="overflow-auto" title={activePopup.title} isOpen={!!activePopup?.type}
                    closeModal={closeModal}
                />
            }
        </>
    )
}

export default Monitoring
